import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
    Mail, Send, Eye, Pencil, Loader2, RefreshCw, CheckCircle2, FileEdit, MailOpen,
    AlertCircle, Calendar, Users, ExternalLink, BarChart3, UserCheck,
} from 'lucide-react';
import RecipientsSelectorDialog from './RecipientsSelectorDialog';

const STATUS_LABELS = {
    borrador: { label: 'Borrador', cls: 'bg-slate-100 text-slate-700', icon: FileEdit },
    listo: { label: 'Listo para enviar', cls: 'bg-amber-100 text-amber-800', icon: AlertCircle },
    enviada: { label: 'Enviada', cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
    activa: { label: 'Activa', cls: 'bg-blue-100 text-blue-800', icon: MailOpen },
};

function StatusBadge({ status }) {
    const meta = STATUS_LABELS[status] || STATUS_LABELS.borrador;
    const Icon = meta.icon;
    return (
        <Badge className={cn('text-[10px] gap-1', meta.cls)}>
            <Icon className="h-3 w-3" />
            {meta.label}
        </Badge>
    );
}

function CampaignCard({ campaign, onPreview, onEdit, onSend, onSelectRecipients, onRefresh, isAdminWelcome }) {
    const [sending, setSending] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const fmtDate = (d) => {
        if (!d) return '—';
        try {
            return new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('es-CL', {
                day: '2-digit', month: 'long', year: 'numeric',
            });
        } catch {
            return d;
        }
    };

    const handleSend = async (dry = false, useTest = false) => {
        const payload = dry ? { dry_run: true } : (useTest && testEmail ? { test_email: testEmail } : {});
        const recipients = useTest ? `(test: ${testEmail})` : `(${campaign.pending_recipients} destinatarios)`;
        const confirmMsg = dry
            ? `Simular envío de "${campaign.label}" ${recipients}?`
            : useTest
                ? `Enviar correo de prueba a ${testEmail}?`
                : `Confirmar envío masivo de "${campaign.label}" a ${campaign.pending_recipients} docente(s)?`;
        if (!dry && !window.confirm(confirmMsg)) return;
        setSending(true);
        try {
            const { data } = await api.post(`/admin/campaigns/${campaign.id}/send`, payload);
            if (data.dry_run) {
                toast.info(`Dry-run: se enviaría a ${data.would_send_to} docente(s).`);
            } else {
                const stats = data.stats || {};
                toast.success(`Envío completado: ${stats.sent}/${stats.total_attempted} OK${stats.errors ? `, ${stats.errors} error(es)` : ''}.`);
            }
            onRefresh();
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Error al ejecutar el envío';
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className={cn(
                'rounded-xl border bg-white p-4 transition-shadow hover:shadow-sm',
                campaign.status === 'enviada' ? 'border-emerald-200' : 'border-slate-200'
            )}
            data-testid={`campaign-card-${campaign.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Mail className="h-4 w-4 text-slate-500" />
                        <p className="font-heading text-sm font-bold text-slate-900">{campaign.label}</p>
                        <StatusBadge status={campaign.status} />
                    </div>
                    <p className="mt-1 text-xs text-slate-600 truncate">
                        <span className="font-medium text-slate-700">Asunto:</span> {campaign.subject}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
                        {campaign.scheduled_date && (
                            <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {fmtDate(campaign.scheduled_date)}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" /> {campaign.pending_recipients} pendiente(s)
                        </span>
                        {campaign.sent_total > 0 && (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                                <CheckCircle2 className="h-3 w-3" /> {campaign.sent_total} enviado(s)
                            </span>
                        )}
                        {campaign.last_send_at && (
                            <span className="text-slate-400">
                                · último envío: {new Date(campaign.last_send_at).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Acciones */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPreview(campaign)}
                    className="gap-1.5"
                    data-testid={`campaign-preview-${campaign.id}`}
                >
                    <Eye className="h-3.5 w-3.5" />
                    Vista previa
                </Button>
                {!isAdminWelcome && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(campaign)}
                        className="gap-1.5"
                        data-testid={`campaign-edit-${campaign.id}`}
                    >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar contenido
                    </Button>
                )}
                {!isAdminWelcome && (
                    <>
                        <div className="flex items-center gap-1.5 ml-auto">
                            <Input
                                placeholder="email@prueba.cl (opcional)"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                className="h-8 w-44 text-xs"
                                data-testid={`campaign-test-email-${campaign.id}`}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSend(false, true)}
                                disabled={sending || !testEmail}
                                className="gap-1.5 text-xs"
                                data-testid={`campaign-test-send-${campaign.id}`}
                                title="Enviar SOLO al email de prueba"
                            >
                                Enviar prueba
                            </Button>
                        </div>
                        <Button
                            size="sm"
                            onClick={() => onSelectRecipients(campaign)}
                            disabled={sending}
                            className="gap-1.5 bg-slate-900 hover:bg-slate-800"
                            data-testid={`campaign-select-recipients-${campaign.id}`}
                        >
                            <UserCheck className="h-3.5 w-3.5" />
                            Seleccionar destinatarios y enviar
                        </Button>
                    </>
                )}
                {isAdminWelcome && (
                    <p className="text-[10px] text-slate-400 italic ml-auto">
                        Esta campaña se gestiona desde el panel de Correos de Bienvenida.
                    </p>
                )}
            </div>

            {/* Stats último envío */}
            {campaign.last_send_stats && (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600">
                    <strong>Último envío:</strong> {campaign.last_send_stats.sent}/{campaign.last_send_stats.total_attempted} OK
                    {campaign.last_send_stats.errors > 0 && (
                        <span className="text-rose-700"> · {campaign.last_send_stats.errors} error(es)</span>
                    )}
                    {campaign.last_send_stats.test_mode && <Badge className="ml-2 bg-amber-100 text-amber-800 text-[9px]">prueba</Badge>}
                </div>
            )}
        </div>
    );
}

function EditDialog({ campaign, open, onClose, onSaved }) {
    const [subject, setSubject] = useState('');
    const [introHtml, setIntroHtml] = useState('');
    const [ctaLabel, setCtaLabel] = useState('');
    const [ctaPath, setCtaPath] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (campaign) {
            setSubject(campaign.subject || '');
            setIntroHtml(campaign.intro_html || '');
            setCtaLabel(campaign.cta_label || '');
            setCtaPath(campaign.cta_path || '');
        }
    }, [campaign]);

    const handleSave = async () => {
        if (!campaign) return;
        setSaving(true);
        try {
            await api.patch(`/admin/campaigns/${campaign.id}`, {
                subject,
                intro_html: introHtml,
                cta_label: ctaLabel,
                cta_path: ctaPath,
                status: 'listo',
            });
            toast.success('Cambios guardados — marcado como "Listo para enviar".');
            onSaved();
            onClose();
        } catch {
            toast.error('No se pudo guardar');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="campaign-edit-dialog">
                <DialogHeader>
                    <DialogTitle>Editar: {campaign?.label}</DialogTitle>
                    <DialogDescription>
                        Los cambios se aplican a TODOS los destinatarios que aún no recibieron este correo.
                        El saludo, las métricas dinámicas y la garantía de "trabajo guardado" se generan
                        automáticamente para cada docente.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Asunto</Label>
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            data-testid="campaign-edit-subject"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Cuerpo (HTML permitido)</Label>
                        <Textarea
                            value={introHtml}
                            onChange={(e) => setIntroHtml(e.target.value)}
                            rows={8}
                            className="font-mono text-xs"
                            data-testid="campaign-edit-intro"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">
                            Tip: usa &lt;p&gt;, &lt;strong&gt; y &lt;em&gt; para formatear. El saludo, la garantía
                            y las métricas se agregan automáticamente.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">Texto del botón CTA</Label>
                            <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} data-testid="campaign-edit-cta-label" />
                        </div>
                        <div>
                            <Label className="text-xs">Ruta del CTA</Label>
                            <Input value={ctaPath} onChange={(e) => setCtaPath(e.target.value)} data-testid="campaign-edit-cta-path" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button onClick={handleSave} disabled={saving} className="gap-1.5">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        Guardar y marcar como Listo
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function CampaignsPanel() {
    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState([]);
    const [radarStats, setRadarStats] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [recipientsTarget, setRecipientsTarget] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewTitle, setPreviewTitle] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [campaignsRes, statsRes] = await Promise.all([
                api.get('/admin/campaigns'),
                api.get('/admin/campaigns/radar-stats').catch(() => ({ data: null })),
            ]);
            setCampaigns(campaignsRes.data.campaigns || []);
            setRadarStats(statsRes.data);
        } catch {
            toast.error('No se pudieron cargar las campañas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePreview = (c) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${c.id}/preview`;
        setPreviewUrl(url);
        setPreviewTitle(c.label);
    };

    return (
        <Card data-testid="campaigns-panel" id="campanas">
            <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <Mail className="h-5 w-5" />
                            Campañas de Correo
                        </CardTitle>
                        <CardDescription>
                            Comunicaciones programadas Early Access. Cada campaña se genera automáticamente
                            con métricas personalizadas y se envía solo cuando tú lo apruebas manualmente.
                        </CardDescription>
                    </div>
                    <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} className="gap-1.5" data-testid="campaigns-refresh">
                        <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                        Refrescar
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Widget Radar Docente — clicks UTM tracking */}
                {radarStats && radarStats.campaigns && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="radar-stats-widget">
                        <div className="flex items-center gap-2 mb-2">
                            <ExternalLink className="h-4 w-4 text-blue-700" />
                            <p className="font-heading text-sm font-bold text-slate-900">
                                Tracking de clicks a Radar Docente
                            </p>
                            <Badge className="bg-blue-100 text-blue-800 text-[9px]">UTM</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mb-3">
                            Cruce con embudo de Borradores. Tasa baseline (todos los usuarios):{' '}
                            <strong>{radarStats.baseline?.drafts_creation_rate_pct ?? 0}%</strong>{' '}
                            ({radarStats.baseline?.users_with_drafts}/{radarStats.baseline?.total_users}) crean borradores.
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left border-b border-blue-200">
                                        <th className="pb-1 pr-3 font-semibold text-slate-600">Campaña</th>
                                        <th className="pb-1 px-2 font-semibold text-slate-600 text-right">Clicks</th>
                                        <th className="pb-1 px-2 font-semibold text-slate-600 text-right">Únicos</th>
                                        <th className="pb-1 px-2 font-semibold text-slate-600 text-right">Crearon borrador</th>
                                        <th className="pb-1 pl-2 font-semibold text-slate-600 text-right">Conv.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {radarStats.campaigns
                                        .filter((c) => c.campaign_id !== 'preview' || c.total_clicks > 0)
                                        .map((c) => {
                                            const isPositive = c.conversion_to_drafts_pct != null && c.conversion_to_drafts_pct >= (radarStats.baseline?.drafts_creation_rate_pct ?? 0);
                                            return (
                                                <tr key={c.campaign_id} className="border-b border-blue-100 last:border-0">
                                                    <td className="py-1.5 pr-3 text-slate-700">
                                                        {c.campaign_id === 'preview' ? 'Preview (admin)' : c.campaign_id.replace('early_access_', '')}
                                                    </td>
                                                    <td className="py-1.5 px-2 text-right font-mono">{c.total_clicks}</td>
                                                    <td className="py-1.5 px-2 text-right font-mono">{c.unique_clickers}</td>
                                                    <td className="py-1.5 px-2 text-right font-mono">{c.clickers_with_drafts}</td>
                                                    <td className="py-1.5 pl-2 text-right font-mono">
                                                        {c.conversion_to_drafts_pct != null ? (
                                                            <span className={cn(isPositive ? 'text-emerald-700 font-semibold' : 'text-slate-500')}>
                                                                {c.conversion_to_drafts_pct}%
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-300">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                            <BarChart3 className="h-3 w-3 inline mr-1" />
                            Cada click en el link &ldquo;www.radardocente.cl&rdquo; del correo se registra con UTM source
                            y se cruza con la creación posterior de borradores en Portafolio.
                        </p>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                ) : (
                    campaigns.map((c) => (
                        <CampaignCard
                            key={c.id}
                            campaign={c}
                            onPreview={handlePreview}
                            onEdit={setEditTarget}
                            onSelectRecipients={setRecipientsTarget}
                            onRefresh={fetchData}
                            isAdminWelcome={c.id === 'welcome'}
                        />
                    ))
                )}
            </CardContent>

            {/* Modal selección de destinatarios */}
            <RecipientsSelectorDialog
                open={!!recipientsTarget}
                campaign={recipientsTarget}
                onClose={() => setRecipientsTarget(null)}
                onSent={fetchData}
            />

            {/* Modal edición */}
            <EditDialog
                campaign={editTarget}
                open={!!editTarget}
                onClose={() => setEditTarget(null)}
                onSaved={fetchData}
            />

            {/* Modal preview */}
            <Dialog open={!!previewUrl} onOpenChange={(o) => !o && setPreviewUrl(null)}>
                <DialogContent className="max-w-3xl h-[80vh] flex flex-col" data-testid="campaign-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>Vista previa: {previewTitle}</DialogTitle>
                        <DialogDescription>
                            Así verá el docente el correo (con sus métricas reales si están disponibles).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-hidden rounded-lg border border-slate-200">
                        <iframe
                            title="preview"
                            src={previewUrl ? `${previewUrl}?t=${Date.now()}` : ''}
                            className="w-full h-full"
                            data-testid="campaign-preview-iframe"
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
